import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Loader2 } from "lucide-react";
import AuthShell from "../components/auth/AuthShell";
import { AppAlert, AppButton, AppField } from "../components/ui/AppField";
import { formatPhoneDisplay } from "../utils/profileHelpers";
import {
  clearAppAuthError,
  resendAppVerificationCode,
  selectAppAuthError,
  selectAppAuthLoading,
  selectAppIsAuthenticated,
  verifyAppEmail,
} from "../redux/appAuthSlice";
import { HOME_PATH } from "../config/products";

const RESEND_SECONDS = 15 * 60;

const VerifyEmail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const loading = useSelector(selectAppAuthLoading);
  const error = useSelector(selectAppAuthError);
  const isAuthenticated = useSelector(selectAppIsAuthenticated);

  const email = location.state?.email || "";
  const phoneNumber = location.state?.phoneNumber || "";
  const contactMethod = location.state?.contactMethod || (email ? "email" : "phone");
  const returnTo = location.state?.from || HOME_PATH;
  const displayContact =
    contactMethod === "phone" ? formatPhoneDisplay(phoneNumber) || phoneNumber : email;

  const [code, setCode] = useState("");
  const [timer, setTimer] = useState(RESEND_SECONDS);
  const [canResend, setCanResend] = useState(false);
  const [resendMessage, setResendMessage] = useState("");
  const [authSplashPending, setAuthSplashPending] = useState(false);

  useEffect(() => {
    if (!email && !phoneNumber) {
      navigate("/app/login", { replace: true });
    }
  }, [email, phoneNumber, navigate]);

  useEffect(() => {
    if (isAuthenticated && !authSplashPending) {
      navigate(returnTo, { replace: true });
    }
  }, [authSplashPending, isAuthenticated, navigate, returnTo]);

  useEffect(() => {
    if (timer <= 0) {
      setCanResend(true);
      return undefined;
    }

    setCanResend(false);
    const interval = window.setInterval(() => {
      setTimer((prev) => Math.max(prev - 1, 0));
    }, 1000);

    return () => window.clearInterval(interval);
  }, [timer]);

  const formatTimer = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!code.trim()) return;

    dispatch(clearAppAuthError());
    setAuthSplashPending(true);
    const payload = { code: code.trim() };
    if (email) payload.email = email;
    if (phoneNumber) payload.phoneNumber = phoneNumber;

    try {
      await dispatch(verifyAppEmail(payload)).unwrap();
      navigate("/app/welcome", { replace: true, state: { to: returnTo } });
    } catch {
      setAuthSplashPending(false);
    }
  };

  const handleResend = async () => {
    if (!canResend) return;

    dispatch(clearAppAuthError());
    setResendMessage("");

    const payload = {};
    if (email) payload.email = email;
    if (phoneNumber) payload.phoneNumber = phoneNumber;

    try {
      await dispatch(resendAppVerificationCode(payload)).unwrap();
      setTimer(RESEND_SECONDS);
      setCanResend(false);
      setResendMessage("A new verification code was sent.");
    } catch {
      /* slice sets error state */
    }
  };

  return (
    <AuthShell
      title="Verify your account"
      subtitle={
        <>
          We sent a code to{" "}
          <span className="font-semibold text-white/85">{displayContact}</span>. Enter it below
          to continue.
        </>
      }
      footer={
        <Link
          to="/app/login"
          className="app-btn-ghost flex h-12 w-full items-center justify-center rounded-lg text-[14px] font-extrabold tracking-tight text-white"
        >
          Back to log in
        </Link>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {error && <AppAlert>{error}</AppAlert>}
        {resendMessage ? (
          <p className="rounded-lg border border-emerald-500/20 bg-emerald-500/10 px-3 py-2 text-[12px] text-emerald-200">
            {resendMessage}
          </p>
        ) : null}

        <AppField
          id="verify-code"
          label="Verification code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          value={code}
          onChange={(e) => {
            setCode(e.target.value.replace(/\s/g, ""));
            if (error) dispatch(clearAppAuthError());
          }}
          className="[&_input]:text-center [&_input]:text-lg [&_input]:tracking-[0.35em] [&_input]:font-semibold"
        />

        <AppButton type="submit" disabled={loading || !code.trim()}>
          {loading ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 size={16} className="animate-spin" />
              Verifying
            </span>
          ) : (
            "Verify and continue"
          )}
        </AppButton>

        <div className="text-center text-[12px] text-white/45">
          {canResend ? (
            <button
              type="button"
              onClick={handleResend}
              disabled={loading}
              className="font-semibold text-[#64b5f6] hover:text-white transition-colors disabled:opacity-50"
            >
              Resend code
            </button>
          ) : (
            <span>Resend available in {formatTimer(timer)}</span>
          )}
        </div>
      </form>
    </AuthShell>
  );
};

export default VerifyEmail;
